import { TinyEmitter } from 'tiny-emitter'
import { IContext, IMutableContext, InMemoryStorageProvider, IToggle, UnleashClient } from 'unleash-proxy-client'
import { isDev, isStaging, unleashKey, unleashProxyUrl } from 'embed/constants/config'
import { isUnleashContext, UnleashContext, UnleashContextProperties } from '../types'

interface ContextUpdate extends UnleashContextProperties {
  leadUuid?: string
}

export class Unleash extends TinyEmitter {
  readonly #unleashClient: UnleashClient
  #ready = false
  #cachedToggles: IToggle[] | undefined = undefined

  constructor() {
    super()

    this.#unleashClient = new UnleashClient({
      appName: 'embed',
      clientKey: unleashKey,
      environment: isDev ? 'development' : isStaging ? 'staging' : 'production',
      storageProvider: new InMemoryStorageProvider(),
      url: unleashProxyUrl,
    })
    this.#unleashClient.on('ready', () => {
      this.#cachedToggles = undefined
      this.#ready = true
      this.emit('ready')
    })
    this.#unleashClient.on('initialized', () => {
      this.#cachedToggles = undefined
      this.emit('initialized')
    })
    this.#unleashClient.on('update', () => {
      this.#cachedToggles = undefined
      this.emit('update')
    })
  }

  async init() {
    await this.#unleashClient.start()
  }

  isReady() {
    return this.#ready
  }

  getToggles(): IToggle[] {
    if (this.#cachedToggles === undefined) {
      this.#cachedToggles = this.#unleashClient.getAllToggles()
    }
    return this.#cachedToggles
  }

  getContext(): UnleashContext {
    const context: IContext | UnleashContext = this.#unleashClient.getContext()
    return isUnleashContext(context) ? context : ({ ...context, properties: {} } as UnleashContext)
  }

  #updateContextAndFetchToggles(context: IMutableContext): void {
    this.#unleashClient.updateContext(context)
  }

  updateContext(contextUpdate: ContextUpdate): void {
    const { leadUuid, ...propertiesToUpdate } = contextUpdate
    const currentProperties = this.getContext().properties
    const newContext: IMutableContext = {
      sessionId: leadUuid,
      properties: {
        ...currentProperties,
        ...propertiesToUpdate,
      },
    }
    this.#updateContextAndFetchToggles(newContext)
  }
}
